import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Loader2, PlugZap, Unplug, CalendarDays, Share2, MessageSquare, Copy, Check, RefreshCw, Lock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { api, errMsg, fmtRel } from "@/lib/api";
import { Pill, Dot } from "@/components/app/shared";

function CopyField({ value, testid }) {
  const [ok, setOk] = useState(false);
  return (
    <div className="flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1.5" data-testid={testid}>
      <div className="flex-1 truncate font-mono text-[11px]">{value}</div>
      <Button variant="ghost" size="icon" className="h-6 w-6" aria-label="Copiar"
        onClick={() => { navigator.clipboard?.writeText(value || ""); setOk(true); setTimeout(() => setOk(false), 1200); }}>
        {ok ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      </Button>
    </div>
  );
}

export default function GoogleWorkspaceCard({ isSuperadmin }) {
  const qc = useQueryClient();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["google-status"],
    queryFn: async () => (await api.get("/google/status")).data,
    refetchInterval: (q) => (q?.state?.data?.connected ? false : 10_000),
  });

  // Honor OAuth callback (?google=connected|error)
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const g = p.get("google");
    if (g === "connected") {
      toast.success("Cuenta de Google conectada");
      p.delete("google"); window.history.replaceState({}, "", `${window.location.pathname}?${p}`);
      refetch();
      qc.invalidateQueries({ queryKey: ["calendar-events"] });
    } else if (g === "error") {
      toast.error("No pudimos conectar tu cuenta de Google. Inténtalo de nuevo.");
      p.delete("google"); window.history.replaceState({}, "", `${window.location.pathname}?${p}`);
    }
  }, [refetch, qc]);

  const connect = useMutation({
    mutationFn: async () => (await api.get("/google/oauth/start")).data,
    onSuccess: (r) => { if (r?.url) window.location.href = r.url; },
    onError: (e) => toast.error(errMsg(e)),
  });
  const disconnect = useMutation({
    mutationFn: async () => api.post("/google/disconnect"),
    onSuccess: () => { toast.success("Cuenta de Google desconectada"); refetch(); qc.invalidateQueries({ queryKey: ["calendar-events"] }); },
  });
  const createEditorial = useMutation({
    mutationFn: async () => (await api.post("/google/editorial-calendar")).data,
    onSuccess: (r) => { toast.success(`Calendario «Mediana · Editorial» listo (${r.synced || 0} eventos sincronizados)`); refetch(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const syncEditorial = useMutation({
    mutationFn: async () => (await api.post("/google/editorial-calendar/sync")).data,
    onSuccess: (r) => toast.success(`Sincronizados ${r.synced || 0} eventos editoriales`),
    onError: (e) => toast.error(errMsg(e)),
  });
  const chatTest = useMutation({
    mutationFn: async () => (await api.post("/google/chat/test")).data,
    onSuccess: () => toast.success("Alerta publicada en Google Chat"),
    onError: (e) => toast.error(errMsg(e)),
  });

  if (isLoading || !data) {
    return <div className="h-56 animate-pulse rounded-lg border border-border bg-card" data-testid="google-card-loading" />;
  }

  const configured = data.configured;
  const connected = data.connected;
  const editorial = data.editorialCalendar;

  return (
    <section className="rounded-lg border border-border bg-card" data-testid="google-settings-card">
      <div className="flex items-center justify-between border-b border-border px-5 py-4">
        <div>
          <div className="text-sm font-semibold">Google Workspace · por persona</div>
          <div className="text-xs text-muted-foreground">
            Cada integrante conecta su propia cuenta de @medianamag.com. Mediana Hub nunca almacena contraseñas.
          </div>
        </div>
        <Pill data-testid="google-status-pill" tone={connected ? "mint" : configured ? "neutral" : "blush"}>
          <Dot tone={connected ? "ok" : configured ? "warn" : "bad"} />
          {connected ? "Tu cuenta está conectada" : configured ? "Listo para conectar" : "Falta configuración"}
        </Pill>
      </div>

      {!configured && (
        <div className="border-b border-border bg-butter px-5 py-3 text-[12.5px] text-ink" data-testid="google-not-configured">
          <div className="flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Falta configurar el Client de Google Cloud.</div>
          <div className="mt-1 text-[11.5px] opacity-80">Un Superadmin debe guardar GOOGLE_CLIENT_ID y GOOGLE_CLIENT_SECRET en las claves del sistema y autorizar el redirect URI exacto que aparece abajo.</div>
        </div>
      )}

      {connected && data.needsReconnect && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-butter/60 px-5 py-3 text-[12.5px]" data-testid="google-needs-reconnect">
          <span>Hay un permiso nuevo: escribir en Drive (para archivar propuestas y evidencia). Reconecta tu cuenta para activarlo.</span>
          <Button size="sm" onClick={() => connect.mutate()} disabled={connect.isPending} data-testid="google-reconnect-button">
            {connect.isPending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1.5 h-3.5 w-3.5" />} Reconectar
          </Button>
        </div>
      )}

      <div className="grid gap-5 p-5 md:grid-cols-2">
        <div className="space-y-3">
          <div className="kicker">Mi conexión</div>
          <div className="rounded-md border border-border bg-background p-3">
            {connected ? (
              <div className="space-y-1.5 text-[13px]">
                <div className="font-mono text-[11.5px]" data-testid="google-connected-email">{data.email}</div>
                <div className="text-[11px] text-muted-foreground">
                  Conectado {fmtRel(data.connectedAt)} · {data.realtime ? "Calendar en tiempo real" : "Calendar en sincronización periódica"}
                </div>
                {data.lastCalendarSync && (
                  <div className="font-mono text-[10.5px] text-muted-foreground">Último barrido: {fmtRel(data.lastCalendarSync)}</div>
                )}
              </div>
            ) : (
              <div className="text-[12.5px] text-muted-foreground">
                Al conectar tu cuenta vas a ver Calendar, Drive, Gmail, Docs y Tasks dentro del Hub. Solo tú verás tu información.
              </div>
            )}
          </div>
          <div className="flex gap-2">
            {connected ? (
              <>
                <Button variant="outline" onClick={() => refetch()} data-testid="google-refresh-button"><RefreshCw className="mr-1.5 h-4 w-4" /> Refrescar</Button>
                <Button variant="ghost" onClick={() => disconnect.mutate()} disabled={disconnect.isPending} data-testid="google-disconnect-button">
                  <Unplug className="mr-1.5 h-4 w-4" /> Desconectar
                </Button>
              </>
            ) : (
              <Button onClick={() => connect.mutate()} disabled={!configured || connect.isPending} data-testid="google-connect-button">
                {connect.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <PlugZap className="mr-1.5 h-4 w-4" />}
                Conectar mi cuenta de Google
              </Button>
            )}
          </div>
          <div className="text-[11px] text-muted-foreground">
            Permisos solicitados: Calendar (eventos propios), Drive (leer y archivar en la unidad del CRM), Docs (crear/leer), Tasks, Gmail (solo envío).
          </div>
        </div>

        <div className="space-y-3">
          <div className="kicker">Redirect URI autorizado</div>
          <CopyField value={data.redirectUri} testid="google-redirect-uri" />
          <div className="text-[11px] text-muted-foreground">
            Agrega esta URI exacta en Google Cloud · APIs y servicios · Credenciales · Client ID OAuth. Si cambia el dominio, pide a un Superadmin actualizar PUBLIC_BASE_URL.
          </div>
          <div className="flex items-center gap-2 pt-1 text-[11px] text-muted-foreground" data-testid="google-connected-count">
            <Share2 className="h-3.5 w-3.5" /> {data.connectedUsers || 0} {data.connectedUsers === 1 ? "persona conectada en Mediana" : "personas conectadas en Mediana"}.
          </div>
        </div>
      </div>

      <Separator />

      <div className="grid gap-5 p-5 md:grid-cols-2">
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-sm font-semibold"><CalendarDays className="h-4 w-4" /> Calendario «Mediana · Editorial»</div>
          <div className="text-[12px] text-muted-foreground">
            Un calendario compartido con todo el dominio para las fechas del Hub: publicaciones, deadlines de pauta y cierre del libro anual.
          </div>
          {editorial?.exists ? (
            <div className="space-y-2">
              <Pill tone="mint" data-testid="editorial-cal-exists"><Dot tone="ok" /> Activo{editorial.ownerIsMe ? " · lo creaste tú" : ""}</Pill>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => syncEditorial.mutate()} disabled={!connected || syncEditorial.isPending} data-testid="editorial-cal-sync">
                  {syncEditorial.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />}
                  Sincronizar ahora
                </Button>
              </div>
            </div>
          ) : (
            <Button onClick={() => createEditorial.mutate()} disabled={!connected || !isSuperadmin || createEditorial.isPending} data-testid="editorial-cal-create">
              {createEditorial.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CalendarDays className="mr-1.5 h-4 w-4" />}
              Crear calendario compartido
            </Button>
          )}
          {!isSuperadmin && !editorial?.exists && (
            <div className="text-[11px] text-muted-foreground">Un Superadmin conectado puede crearlo.</div>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-sm font-semibold"><MessageSquare className="h-4 w-4" /> Alertas en Google Chat</div>
          <div className="text-[12px] text-muted-foreground">
            Opcional. Si guardaste un webhook de un espacio de Chat, el Hub puede publicar avisos (p. ej., aceptaciones de cotización).
          </div>
          <Pill tone={data.chatConfigured ? "mint" : "neutral"} data-testid="google-chat-status">
            <Dot tone={data.chatConfigured ? "ok" : "muted"} />
            {data.chatConfigured ? "Webhook configurado" : "Webhook sin configurar"}
          </Pill>
          <div>
            <Button variant="outline" onClick={() => chatTest.mutate()} disabled={!data.chatConfigured || !isSuperadmin || chatTest.isPending} data-testid="google-chat-test">
              {chatTest.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <MessageSquare className="mr-1.5 h-4 w-4" />}
              Enviar prueba
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
